import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { UserContext } from '../../auth/UserContext';
import { ENABLE_DEEP_LINK_DEBUG } from '../constants';
import type {
    DeepLinkContextValue,
    DeepLinkProviderProps,
    DeepLinkUIStateStore,
    GetDeepLinkInitialStateOptions,
    GetDeepLinkRuntimeStateOptions
} from '../types';
import { DeepLinkUIStateDebugMonitor } from './components/DeepLinkUIStateDebugMonitor';
import { DeepLinkContext } from './contexts/DeepLinkContext';
import { useInitialStateConsumptionLedger } from './hooks/useInitialStateConsumptionLedger';
import {
    buildDeepLinkURLFromState,
    deepLinkLog,
    getRoutePathFromDeepLinkUIState,
    getValueAtPath,
    parseUIStateFromSearch,
    removeValueAtPath,
    setValueAtPath
} from './utils';

export default function DeepLinkProvider({ children }: DeepLinkProviderProps) {
    const initialUIStateFromURL = useMemo(() => parseUIStateFromSearch(), []);

    /**
     * This ref holds the runtime deep link UI state.
     * It is actively read from and written to as the user interacts with deep link enabled UI surfaces.
     *
     * It is initialized when a particular entity view component mounts, either during application bootstrap or in
     * response to a client side navigation.
     */
    const uiRuntimeStateRef = useRef<DeepLinkUIStateStore>({ ui: {} });

    /**
     * This ref holds the initial deep link UI state that is parsed from the URL on application bootstrap.
     *
     * Thereafter, it is intended only to be read from for state initialization of relevant UI surfaces during
     * their mounting lifecycle. Furthermore, it is read via a consume-once-ledger mechanism to ensure that the initial
     * state is only used for the very first mount of the relevant UI surfaces, and not for subsequent re-renders or
     * mounts (e.g. due to client side navigation).
     */
    const uiInitialStateRef = useRef<DeepLinkUIStateStore>({
        ui: initialUIStateFromURL
    });

    const { isInitialPathConsumed, consumeInitialPath, getConsumedInitialPaths } = useInitialStateConsumptionLedger();

    const { application } = useContext(UserContext);

    useEffect(() => {
        deepLinkLog('info', 'DeepLinkProvider initialized.', {
            uiRuntimeState: JSON.stringify(uiRuntimeStateRef.current),
            uiInitialState: JSON.stringify(uiInitialStateRef.current)
        });
    }, []);

    const getDeepLinkInitialState = useCallback(
        <T = unknown,>(options: GetDeepLinkInitialStateOptions): T | undefined => {
            deepLinkLog('group', `DeepLinkProvider[${options.path}]->getDeepLinkInitialState()`);

            if (isInitialPathConsumed(options.path)) {
                deepLinkLog('trace', `Initial UI state slice for path ${options.path} already consumed.`);
                deepLinkLog('groupEnd');
                return;
            }

            const uiState = uiInitialStateRef.current?.ui;

            if (!uiState) {
                console.warn('No active initial state found when getting deep link initial state');
                deepLinkLog('groupEnd');
                return;
            }

            const stateSlice = getValueAtPath<T | undefined>(uiState, options.path);

            deepLinkLog('trace', `Deep link initial state got for path ${options.path} with value:`, stateSlice);

            if (options.clearAfterRead) {
                consumeInitialPath(options.path);
                deepLinkLog('info', `Deep link initial state softly consumed for path ${options.path}.`);
            }

            deepLinkLog('groupEnd');
            return stateSlice;
        },
        [consumeInitialPath, isInitialPathConsumed]
    );

    const getDeepLinkRuntimeState = useCallback(
        <T = unknown,>(options: GetDeepLinkRuntimeStateOptions): T | undefined => {
            deepLinkLog('group', `DeepLinkProvider[${options.path}]->getDeepLinkRuntimeState()`);

            const uiState = uiRuntimeStateRef.current?.ui;

            if (!uiState) {
                console.warn('No active runtime state found when getting deep link runtime state');
                deepLinkLog('groupEnd');
                return;
            }

            const stateSlice = getValueAtPath<T | undefined>(uiState, options.path);

            deepLinkLog('trace', `Deep link runtime state got for path ${options.path} with value:`, stateSlice);

            deepLinkLog('groupEnd');
            return stateSlice;
        },
        []
    );

    const setDeepLinkRuntimeState = useCallback<DeepLinkContextValue['setDeepLinkRuntimeState']>((value, options) => {
        deepLinkLog('group', `DeepLinkProvider[${options.path}]->setDeepLinkRuntimeState()`);

        if (value === undefined || value === null) {
            console.warn('No value provided when setting deep link runtime state');
            deepLinkLog('groupEnd');
            return;
        }

        const uiState = uiRuntimeStateRef.current?.ui;

        if (!uiState) {
            console.warn('No active runtime state found when setting deep link runtime state');
            deepLinkLog('groupEnd');
            return;
        }

        const nextUIState = setValueAtPath(uiState, options.path, value, options.merge);

        uiRuntimeStateRef.current.ui = nextUIState ?? {};

        deepLinkLog(
            'trace',

            `Deep link runtime state set for path "${options.path}" with value:`,
            value,
            `, new runtime state:`,
            nextUIState
        );
        deepLinkLog('groupEnd');
    }, []);

    const clearDeepLinkRuntimeState = useCallback<DeepLinkContextValue['clearDeepLinkRuntimeState']>((path) => {
        deepLinkLog('group', `DeepLinkProvider[${path}]->clearDeepLinkRuntimeState()`);

        const uiState = uiRuntimeStateRef.current?.ui;

        if (!uiState) {
            console.warn('No active runtime state found when clearing deep link runtime state');
            deepLinkLog('groupEnd');
            return;
        }

        const nextUIState = removeValueAtPath(uiState, path);

        uiRuntimeStateRef.current.ui = nextUIState;

        deepLinkLog('trace', `Deep link runtime state cleared for path "${path}", new runtime state:`, nextUIState);
        deepLinkLog('groupEnd');
    }, []);

    const buildDeepLinkURL = useCallback<DeepLinkContextValue['buildDeepLinkURL']>(
        (options) => {
            const activeUiState = uiRuntimeStateRef.current?.ui;

            if (!activeUiState) {
                console.warn('No active UI state found when building deep link URL');
                return;
            }

            const applicationId = application?.id;
            if (!applicationId) {
                console.warn('No runtime application id found when building deep link URL');
                return;
            }

            const deepLinkURL = buildDeepLinkURLFromState(
                activeUiState,
                options.path,
                applicationId,
                options.overrideStateSlice
            );

            return deepLinkURL;
        },
        [application?.id]
    );

    const getUIStateForDebugMonitor = useCallback(() => {
        return {
            uiInitialState: uiInitialStateRef.current?.ui,
            uiRuntimeState: uiRuntimeStateRef.current?.ui,
            consumptionLedger: {
                initial: getConsumedInitialPaths()
            }
        };
    }, [getConsumedInitialPaths]);

    const getResolvedDeepLinkRoutePath = useCallback<DeepLinkContextValue['getResolvedDeepLinkRoutePath']>(() => {
        return getRoutePathFromDeepLinkUIState(uiInitialStateRef.current.ui);
    }, []);

    const contextValue = useMemo<DeepLinkContextValue>(() => {
        return {
            getDeepLinkInitialState,
            getDeepLinkRuntimeState,
            setDeepLinkRuntimeState,
            clearDeepLinkRuntimeState,
            buildDeepLinkURL,
            getResolvedDeepLinkRoutePath
        };
    }, [
        buildDeepLinkURL,
        clearDeepLinkRuntimeState,
        getDeepLinkInitialState,
        getDeepLinkRuntimeState,
        getResolvedDeepLinkRoutePath,
        setDeepLinkRuntimeState
    ]);

    return (
        <DeepLinkContext value={contextValue}>
            {children}
            {ENABLE_DEEP_LINK_DEBUG && (
                <DeepLinkUIStateDebugMonitor
                    applicationName={application?.id}
                    getUIStateForDebugMonitor={getUIStateForDebugMonitor}
                />
            )}
        </DeepLinkContext>
    );
}
